/**
 * GST Report Aggregation & Calculation Service
 * Generates audit-ready data structures for GSTR-1 (Sales) and GSTR-3B (Summary & ITC)
 */
import { normalizeDate, computeNetTax } from "./gstCalculations.js";

export { normalizeDate };

/**
 * Calculates complete GST Report metrics from invoices and purchases
 */
export function calculateGstReport({ invoices = [], purchases = [], fromDate, toDate, companyId = null }) {
  const normFrom = normalizeDate(fromDate);
  const normTo = normalizeDate(toDate);

  const filteredInvoices = invoices.filter((inv) => {
    if (companyId && inv.companyId && inv.companyId !== companyId) return false;
    const invDate = normalizeDate(inv.invoiceDate || inv.date || inv.createdAt);
    if (!invDate) return false;
    if (normFrom && invDate < normFrom) return false;
    if (normTo && invDate > normTo) return false;
    return true;
  });

  const filteredPurchases = purchases.filter((pur) => {
    if (companyId && pur.companyId && pur.companyId !== companyId) return false;
    const purDate = normalizeDate(pur.billDate || pur.date || pur.createdAt);
    if (!purDate) return false;
    if (normFrom && purDate < normFrom) return false;
    if (normTo && purDate > normTo) return false;
    return true;
  });

  const b2bInvoices = [];
  const b2cInvoices = [];
  const hsnMap = new Map();

  let totalSalesTaxable = 0;
  let totalSalesCgst = 0;
  let totalSalesSgst = 0;
  let totalSalesIgst = 0;
  let totalSalesGross = 0;
  let totalNonGstSales = 0;

  filteredInvoices.forEach((inv) => {
    const isNormal = inv.isNonGst || String(inv.invoiceType || "").toLowerCase() === "normal";
    const grandTotal = Number(inv.grandTotal || 0);
    const subtotal = Number(inv.subtotal || 0);
    const cgst = isNormal ? 0 : Number(inv.cgst || 0);
    const sgst = isNormal ? 0 : Number(inv.sgst || 0);
    const igst = isNormal ? 0 : Number(inv.igst || 0);

    totalSalesGross += grandTotal;
    if (isNormal) {
      totalNonGstSales += grandTotal;
    } else {
      totalSalesTaxable += subtotal;
      totalSalesCgst += cgst;
      totalSalesSgst += sgst;
      totalSalesIgst += igst;
    }

    const customerGstin = (inv.customerGstin || "").trim().toUpperCase();
    const isB2B = Boolean(customerGstin && customerGstin.length >= 15);

    const row = {
      id: inv.id,
      invoiceNo: inv.invoiceNo || "N/A",
      invoiceDate: normalizeDate(inv.invoiceDate || inv.date),
      customerName: inv.customerName || "Cash Customer",
      customerGstin: customerGstin || "Unregistered",
      placeOfSupply: inv.customerStateCode || (customerGstin ? customerGstin.slice(0, 2) : "33"),
      isInterState: Boolean(inv.isInterState),
      taxableValue: subtotal,
      cgst,
      sgst,
      igst,
      totalTax: cgst + sgst + igst,
      grandTotal,
      isNormal
    };

    if (isB2B && !isNormal) b2bInvoices.push(row);
    else b2cInvoices.push(row);

    if (!isNormal && Array.isArray(inv.lineItems)) {
      inv.lineItems.forEach((li) => {
        const code = (li.gstCode || "OTHER").trim() || "OTHER";
        const qty = Number(li.qty || 1);
        const taxable = Number(li.lineSubtotal || 0);
        const gstAmount = Number(li.lineGst || 0);

        if (!hsnMap.has(code)) {
          hsnMap.set(code, {
            hsnCode: code,
            description: li.itemName || "General Goods",
            unit: li.unit || "pcs",
            totalQty: 0,
            rate: Number(li.gstPercent || 0),
            taxableValue: 0,
            cgst: 0,
            sgst: 0,
            igst: 0,
            totalValue: 0
          });
        }
        const entry = hsnMap.get(code);
        entry.totalQty += qty;
        entry.taxableValue += taxable;
        entry.totalValue += (taxable + gstAmount);
        if (inv.isInterState) {
          entry.igst += gstAmount;
        } else {
          entry.cgst += Math.round((gstAmount / 2) * 100) / 100;
          entry.sgst += Math.round((gstAmount / 2) * 100) / 100;
        }
      });
    }
  });

  const b2cRateMap = new Map();
  b2cInvoices.forEach((inv) => {
    const key = `${inv.placeOfSupply}_${inv.isNormal ? "0" : "MIXED"}`;
    if (!b2cRateMap.has(key)) {
      b2cRateMap.set(key, {
        pos: inv.placeOfSupply,
        type: inv.isNormal ? "Non-GST / Exempt" : "Taxable Retail",
        taxableValue: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        total: 0,
        count: 0
      });
    }
    const it = b2cRateMap.get(key);
    it.taxableValue += inv.taxableValue;
    it.cgst += inv.cgst;
    it.sgst += inv.sgst;
    it.igst += inv.igst;
    it.total += inv.grandTotal;
    it.count += 1;
  });

  let totalPurchasesTaxable = 0;
  let totalPurchasesCgst = 0;
  let totalPurchasesSgst = 0;
  let totalPurchasesIgst = 0;
  let totalPurchasesGross = 0;
  let totalPurchasesTcs = 0; // TCS paid to vendors: income-tax credit, not GST ITC

  const purchaseList = filteredPurchases.map((pur) => {
    const isNormal = pur.isNonGst || String(pur.purchaseType || "").toLowerCase() === "normal";
    const grandTotal = Number(pur.grandTotal || 0);
    const taxable = Number(pur.subtotal || pur.taxableAmount || 0);
    const cgst = isNormal ? 0 : Number(pur.cgst || 0);
    const sgst = isNormal ? 0 : Number(pur.sgst || 0);
    const igst = isNormal ? 0 : Number(pur.igst || 0);
    const tcs = Number(pur.tcsAmount || 0);

    totalPurchasesGross += grandTotal;
    totalPurchasesTcs += tcs;
    if (!isNormal) {
      totalPurchasesTaxable += taxable;
      totalPurchasesCgst += cgst;
      totalPurchasesSgst += sgst;
      totalPurchasesIgst += igst;
    }

    return {
      id: pur.id,
      vendorBillNo: pur.vendorBillNo || "N/A",
      billDate: normalizeDate(pur.billDate || pur.date),
      vendorName: pur.vendorName || "Vendor",
      vendorGstin: (pur.vendorGstin || "Unregistered").trim().toUpperCase(),
      taxableValue: taxable,
      cgst,
      sgst,
      igst,
      totalTax: cgst + sgst + igst,
      tcs,
      grandTotal,
      isNormal
    };
  });

  const outputTotal = { cgst: totalSalesCgst, sgst: totalSalesSgst, igst: totalSalesIgst, total: totalSalesCgst + totalSalesSgst + totalSalesIgst };
  const itcTotal = { cgst: totalPurchasesCgst, sgst: totalPurchasesSgst, igst: totalPurchasesIgst, total: totalPurchasesCgst + totalPurchasesSgst + totalPurchasesIgst };
  const netPayable = computeNetTax(outputTotal, itcTotal);

  const invoiceNumbers = filteredInvoices.map((i) => i.invoiceNo).filter(Boolean).sort();

  return {
    period: { fromDate: normFrom, toDate: normTo },
    summary: {
      totalSalesGross,
      totalSalesTaxable,
      totalNonGstSales,
      salesTax: outputTotal,
      totalPurchasesGross,
      totalPurchasesTaxable,
      totalPurchasesTcs: Math.round(totalPurchasesTcs * 100) / 100,
      purchasesItc: itcTotal,
      netPayable,
      counts: {
        totalInvoices: filteredInvoices.length,
        b2bCount: b2bInvoices.length,
        b2cCount: b2cInvoices.length,
        purchasesCount: filteredPurchases.length
      }
    },
    tables: {
      b2bInvoices,
      b2cSummary: Array.from(b2cRateMap.values()),
      hsnSummary: Array.from(hsnMap.values()),
      purchaseRegister: purchaseList,
      docSummary: {
        startNo: invoiceNumbers[0] || "N/A",
        endNo: invoiceNumbers[invoiceNumbers.length - 1] || "N/A",
        totalCount: invoiceNumbers.length,
        cancelledCount: 0
      }
    }
  };
}
